import { supabase } from './supabase';
import { getUserBusinessId } from './supabaseDb';

type DbResult<T> = { data: T | null; error: string | null };

function handleError(err: any): string {
  if (!err) return 'Bilinmeyen hata';
  return err.message || err.details || String(err);
}

export interface PersonalLoan {
  id: string;
  business_id: string;
  loan_no: string;
  borrower_type: 'customer' | 'employee' | 'supplier' | 'other';
  borrower_customer_id: string | null;
  borrower_employee_id: string | null;
  borrower_name: string;
  borrower_phone: string | null;
  borrower_second_phone: string | null;
  borrower_address: string | null;
  principal_amount: number;
  paid_amount: number;
  remaining_amount: number;
  loan_date: string;
  due_date: string | null;
  status: string;
  payment_plan_type: string;
  description: string | null;
  reason: string | null;
  guarantor_name: string | null;
  guarantor_phone: string | null;
  cash_account_id: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  cancelled_at: string | null;
  cancelled_by: string | null;
  cancellation_reason: string | null;
}

export interface PersonalLoanMovement {
  id: string;
  business_id: string;
  loan_id: string;
  movement_type: string;
  amount: number;
  payment_method: string | null;
  cash_account_id: string | null;
  movement_date: string;
  description: string | null;
  receipt_no: string | null;
  created_by: string | null;
  created_at: string;
  reversed_at: string | null;
  reversed_by: string | null;
  reversal_reason: string | null;
  reversal_of: string | null;
}

export interface PersonalLoanSummary {
  active_count: number;
  total_given: number;
  total_repaid: number;
  total_remaining: number;
  overdue_count: number;
  overdue_amount: number;
  this_month_given: number;
  this_month_repaid: number;
}

export async function getPersonalLoans(): Promise<DbResult<PersonalLoan[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('personal_loans')
      .select('*')
      .eq('business_id', bizId)
      .order('created_at', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as PersonalLoan[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getPersonalLoanById(id: string): Promise<DbResult<PersonalLoan>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('personal_loans')
      .select('*')
      .eq('id', id)
      .eq('business_id', bizId)
      .maybeSingle();

    if (error) return { data: null, error: handleError(error) };
    return { data: data as PersonalLoan, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getPersonalLoanMovements(loanId: string): Promise<DbResult<PersonalLoanMovement[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('personal_loan_movements')
      .select('*')
      .eq('loan_id', loanId)
      .eq('business_id', bizId)
      .order('created_at', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as PersonalLoanMovement[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getPersonalLoanSummary(): Promise<DbResult<PersonalLoanSummary>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('get_personal_loan_summary', {
      p_business_id: bizId,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as PersonalLoanSummary, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createPersonalLoan(payload: Record<string, any>): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('create_personal_loan_transaction', {
      payload: { ...payload, business_id: bizId },
    });

    if (error) return { data: null, error: handleError(error) };

    const result = data as any;
    if (!result || result.success === false) {
      return { data: null, error: (result && result.error) || 'Borç kaydı oluşturulamadı.' };
    }

    return { data: result, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function recordRepayment(payload: Record<string, any>): Promise<DbResult<any>> {
  try {
    const { data, error } = await supabase.rpc('record_personal_loan_repayment', {
      payload,
    });

    if (error) return { data: null, error: handleError(error) };

    const result = data as any;
    if (!result || result.success === false) {
      return { data: null, error: (result && result.error) || 'Ödeme kaydedilemedi.' };
    }

    return { data: result, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function cancelPersonalLoan(payload: Record<string, any>): Promise<DbResult<any>> {
  try {
    const { data, error } = await supabase.rpc('cancel_personal_loan', {
      payload,
    });

    if (error) return { data: null, error: handleError(error) };

    const result = data as any;
    if (!result || result.success === false) {
      return { data: null, error: (result && result.error) || 'İptal edilemedi.' };
    }

    return { data: result, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function reverseMovement(payload: Record<string, any>): Promise<DbResult<any>> {
  try {
    const { data, error } = await supabase.rpc('reverse_personal_loan_movement', {
      payload,
    });

    if (error) return { data: null, error: handleError(error) };

    const result = data as any;
    if (!result || result.success === false) {
      return { data: null, error: (result && result.error) || 'Ters kayıt yapılamadı.' };
    }

    return { data: result, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getPersonalLoansByCustomerId(customerId: string): Promise<DbResult<PersonalLoan[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('personal_loans')
      .select('*')
      .eq('business_id', bizId)
      .eq('borrower_customer_id', customerId)
      .order('created_at', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as PersonalLoan[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}